import React from "react";
import { clsx } from "clsx";
import { useInView } from "../../hooks/useInView";

export type SectionVariant =
  | "darkGradientA"
  | "lightPastel"
  | "darkSolid"
  | "darkGradientB"
  // ✅ NEW
  | "smoothSolid";

interface SectionProps {
  id?: string;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  className?: string;

  /**
   * Background rhythm: darkGradientA (hero-style), lightPastel (mild tint), darkSolid, darkGradientB.
   * Use for alternating dark → light → dark → light.
   */
  variant?: SectionVariant;

  /** Optional: animate section on scroll (respects prefers-reduced-motion) */
  animateOnScroll?: boolean;

  // ✅ NEW (for Section 3 background image)
  bgImage?: string; // e.g. "/images/tkm_land_section3.jpeg"
  overlay?: "none" | "dark" | "soft";
}

const variantStyles: Record<Exclude<SectionVariant, "smoothSolid">, string> = {
  darkGradientA:
    "bg-gradient-to-b from-indigo-600/90 via-indigo-700/85 to-slate-950 text-white [&_.section-title]:text-white [&_.section-subtitle]:text-indigo-200/90 [&_.section-title]:[--tw-prose-invert:1] border-t border-white/10",
  lightPastel:
    "text-gray-900 [&_.section-title]:text-gray-900 [&_.section-subtitle]:text-gray-600 [&_a]:text-indigo-600 [&_a:hover]:text-indigo-700 border-t border-[var(--section-divider-light)] dark:border-slate-700/50 dark:text-slate-200 dark:[&_.section-title]:text-white dark:[&_.section-subtitle]:text-slate-300 dark:[&_a]:text-indigo-300 dark:[&_a:hover]:text-indigo-200",
  darkSolid:
    "bg-slate-900 text-white [&_.section-title]:text-white [&_.section-subtitle]:text-slate-300 [&_a]:text-indigo-300 [&_a:hover]:text-indigo-200 [&_.text-app-text]:text-white [&_.text-app-text-muted]:text-slate-400 border-t border-white/10",
  darkGradientB:
    "bg-gradient-to-b from-indigo-950 via-slate-900 to-slate-950 text-white [&_.section-title]:text-white [&_.section-subtitle]:text-slate-300 [&_a]:text-indigo-300 [&_a:hover]:text-indigo-200 [&_.text-app-text]:text-white [&_.text-app-text-muted]:text-slate-400 border-t border-white/10",
};

export const Section: React.FC<SectionProps> = ({
  id,
  title,
  subtitle,
  children,
  className,
  variant = "lightPastel",
  animateOnScroll = true,
  bgImage,
  overlay = "none",
}) => {
  const { ref, inView } = useInView({
    rootMargin: "0px 0px -60px 0px",
    threshold: 0.08,
    triggerOnce: true,
  });

  // ✅ smoothSolid: always dark text on image
  const isSmooth = variant === "smoothSolid";
  const isLight = variant === "lightPastel";
  const bgClass = isSmooth
    ? "text-white border-t border-white/10"
    : isLight
    ? `section-light-pastel-bg ${variantStyles.lightPastel}`
    : variantStyles[variant as Exclude<SectionVariant, "smoothSolid">];

  const overlayClass =
    overlay === "dark"
      ? "bg-black/55"
      : overlay === "soft"
      ? "bg-slate-950/35"
      : "";

  return (
    <section
      id={id}
      ref={animateOnScroll ? ref : undefined}
      className={clsx(
        "py-14 sm:py-20 scroll-mt-20 relative overflow-hidden",
        bgClass,
        animateOnScroll && "transition-all duration-700 ease-out",
        animateOnScroll && (inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"),
        !animateOnScroll && "opacity-100 translate-y-0",
        className
      )}
    >
      {/* ✅ Background image only for smoothSolid (Section 3) */}
      {isSmooth && bgImage ? (
        <div className="absolute inset-0" aria-hidden>
          <div
            className="absolute inset-0 bg-center bg-cover"
            style={{ backgroundImage: `url(${bgImage})` }}
          />
          {overlay !== "none" && (
            <div className={clsx("absolute inset-0", overlayClass)} />
          )}
          {/* vignette gradient to match your reference */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/15 to-black/55" />
        </div>
      ) : null}

      {/* Existing decorative blobs for other variants */}
      {variant === "darkSolid" && !isSmooth && (
        <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
          <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-indigo-500/10 blur-3xl" />
          <div className="absolute -bottom-24 -left-24 h-48 w-48 rounded-full bg-purple-500/8 blur-3xl" />
        </div>
      )}
      {variant === "darkGradientB" && !isSmooth && (
        <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
          <div className="absolute top-1/4 right-0 h-72 w-72 rounded-full bg-indigo-600/12 blur-3xl" />
          <div className="absolute bottom-1/4 left-0 h-56 w-56 rounded-full bg-slate-600/10 blur-3xl" />
        </div>
      )}

      <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <header className="mb-10 sm:mb-12 text-center">
          <h2
            className={clsx(
              "section-title text-3xl font-extrabold tracking-tight sm:text-4xl",
              isSmooth &&
                "text-white drop-shadow-[0_6px_22px_rgba(0,0,0,0.80)]"
            )}
          >
            {title}
          </h2>

          {subtitle && (
            <p
              className={clsx(
                "section-subtitle mt-3 text-lg max-w-2xl mx-auto",
                isSmooth &&
                  "text-white/95 font-semibold drop-shadow-[0_5px_18px_rgba(0,0,0,0.75)]"
              )}
            >
              {subtitle}
            </p>
          )}
        </header>

        {children}
      </div>
    </section>
  );
};
